import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '../SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PROD_ORIGIN = 'https://beta.myinvestfolio.io';

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

const PerspectiveDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/perspectives/${slug}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then((d) => { if (d) setArticle(d); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const ogImage = article ? `${PROD_ORIGIN}/og/${slug}.png` : undefined;

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title={article?.title || 'Perspective'}
        description={article?.summary || 'A perspective from MyInvestFolio.'}
        path={`/perspectives/${slug}`}
        ogImage={ogImage}
        jsonLd={article ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          author: { '@type': 'Person', name: article.author || 'Adejare R. OlaOluwa' },
          datePublished: article.date,
          image: ogImage,
          publisher: {
            '@type': 'Organization',
            name: 'MyInvestFolio',
            logo: { '@type': 'ImageObject', url: `${PROD_ORIGIN}/og/default.png` },
          },
        } : undefined}
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/perspectives')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> All Perspectives
            </Button>
            <span className="text-xl font-bold">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-3xl">
        {loading && <div className="text-center text-gray-400 py-12">Loading…</div>}

        {notFound && (
          <div className="text-center bg-gray-800/60 border border-gray-700 rounded-xl p-16">
            <h1 className="text-3xl font-bold text-emerald-400 mb-3">Perspective not found</h1>
            <p className="text-gray-300 mb-6">This piece may have been moved or has not yet been published.</p>
            <Button onClick={() => navigate('/perspectives')} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-lg">
              See all perspectives
            </Button>
          </div>
        )}

        {article && (
          <article data-testid="perspective-article">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">{article.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
              {article.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(article.date)}</span>}
              {article.reading_time && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{article.reading_time} min read</span>}
              {article.author && <span className="text-emerald-400">{article.author}</span>}
            </div>
            <div className="w-16 h-1 bg-emerald-500 mb-10"></div>

            <div
              className="perspective-prose text-gray-200 leading-relaxed text-lg"
              dangerouslySetInnerHTML={{ __html: article.body_html }}
            />

            {article.linkedin_url && article.linkedin_first_published_at && (
              <div className="mt-12 p-6 bg-gray-800/80 border border-gray-700 rounded-xl text-sm text-gray-300" data-testid="linkedin-backlink">
                This piece first appeared on LinkedIn on {formatDate(article.linkedin_first_published_at)}.{' '}
                <a href={article.linkedin_url} target="_blank" rel="noopener noreferrer"
                  className="text-emerald-400 underline hover:text-emerald-300 inline-flex items-center gap-1">
                  Continue the discussion <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </article>
        )}
      </main>

      <style>{`
        .perspective-prose h2 { color: #34d399; font-size: 1.6rem; font-weight: 600; margin: 2rem 0 1rem; }
        .perspective-prose h3 { color: #6ee7b7; font-size: 1.3rem; font-weight: 600; margin: 1.5rem 0 0.75rem; }
        .perspective-prose p  { margin-bottom: 1.25rem; }
        .perspective-prose blockquote { border-left: 3px solid #10b981; padding-left: 1.25rem; color: #cbd5e1; font-style: italic; margin: 1.5rem 0; }
        .perspective-prose ul, .perspective-prose ol { margin: 1rem 0 1.5rem 1.5rem; }
        .perspective-prose ul li { list-style: disc; margin-bottom: 0.5rem; }
        .perspective-prose ol li { list-style: decimal; margin-bottom: 0.5rem; }
        .perspective-prose a  { color: #34d399; text-decoration: underline; }
        .perspective-prose a:hover { color: #6ee7b7; }
        .perspective-prose code { background: #1f2937; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.9em; }
      `}</style>
    </div>
  );
};

export default PerspectiveDetail;
